<template>
  <div class="page-no-nav flex">
    <div
      v-if="project && (offer ? remainingTime : true)"
      class="container flex grow flex-col lg:pt-20"
    >
      <Back :to="-1" :title="$t('simulation.title')" />
      <p class="mb-8 text-left text-sm text-grey-2/80 lg:mb-12">
        {{ $t('simulation.text') }}
      </p>
      <p v-if="!offer" class="mb-4 text-left text-sm font-bold text-grey-2">
        {{ $t('simulation.input_label') }}
      </p>
      <Form
        ref="form"
        v-slot="{ errors, meta }"
        class="form mb-12"
        @submit="simulate"
      >
        <GeneralInput
          v-model:data="amount"
          noMargin
          type="dollar"
          name="amount"
          :label="$t('simulation.input_placeholder') + (offer ? ' ($)' : '')"
          :error="errors.amount"
          :rules="simulatedAmountRules"
          @keyup.enter="
            () => {
              if (!meta.valid) simulated = null
            }
          "
        />
      </Form>
      <div v-if="simulated">
        <!-- Total assets -->
        <p class="mb-6 text-left text-sm font-bold text-grey-2 lg:mb-4">
          {{ $t('simulation.summary.title') }}
        </p>
        <p class="text-left text-xl font-bold text-grey-2/80">
          ${{ dollarFormatting(simulated.paymentsSum) }}
        </p>
        <p class="mb-6 text-left text-sm text-grey-2/50">
          {{ $t('simulation.summary.total_assets') }}
          {{ monthFormat(duration) }}
        </p>
        <div class="mb-6 flex flex-col items-start gap-3 rounded-2xl lg:mb-4 lg:bg-white lg:p-6">
          <!-- Gross interest -->
          <div class="flex w-full items-center justify-between">
            <p class="text-sm text-grey-2/80">{{ $t('simulation.summary.gross_interest') }}</p>
            <p class="text-sm font-bold text-grey-2/80">
              ${{ dollarFormatting(simulated.interestGrossSum) }}
            </p>
          </div>
          <!-- Payment during grace period -->
          <div v-if="simulated.gracePayments > 0" class="flex w-full items-center justify-between">
            <p class="text-sm text-grey-2/80">{{ $t('simulation.summary.pay_during_grace') }}</p>
            <p class="text-sm font-bold text-grey-2/80 lg:font-semibold lg:text-grey-2">
              ${{ dollarFormatting(simulated.payDuringGrace) }}
            </p>
          </div>
          <!-- Payment after grace / periodic / single -->
          <div class="flex w-full items-center justify-between">
            <p v-if="simulated.gracePayments > 0" class="text-sm text-grey-2/80">
              {{ $t('simulation.summary.pay_after_grace') }}
            </p>
            <p v-else-if="simulated.totalPayments === 1" class="text-sm text-grey-2/80">
              {{ $t('simulation.summary.single_payment') }}
            </p>
            <p v-else class="text-sm text-grey-2/80">
              {{ $t('simulation.summary.periodic_payment') }}
            </p>
            <p class="text-sm font-bold text-grey-2/80 lg:font-semibold lg:text-grey-2">
              ${{ dollarFormatting(simulated.payAfterGrace) }}
            </p>
          </div>
          <!-- Last payment of capital (balloon only) -->
          <div
            v-if="simulated.lastPayment"
            data-test="simulation-last-payment-row"
            class="flex w-full items-center justify-between"
          >
            <p class="text-sm text-grey-2/80">{{ $t('simulation.summary.last_payment_capital') }}</p>
            <p
              data-test="simulation-last-payment-value"
              class="text-sm font-bold text-grey-2/80 lg:font-semibold lg:text-grey-2"
            >
              ${{ dollarFormatting(simulated.lastPayment) }}
            </p>
          </div>
          <!-- Separator -->
          <div class="h-px w-full bg-grey-5" />
          <!-- First payment -->
          <div class="flex w-full items-center justify-between">
            <p v-if="simulated.totalPayments === 1" class="text-sm text-grey-2/80">
              {{ $t('simulation.summary.first_last_payment') }}
            </p>
            <p v-else class="text-sm text-grey-2/80">{{ $t('simulation.summary.first_payment') }}</p>
            <p class="text-sm font-bold text-grey-2/80 lg:font-semibold lg:text-grey-2">
              {{ $t('wording.after') }} {{ monthFormat(12 / project.funding_rate.periodicity) }}
            </p>
          </div>
          <!-- Last payment -->
          <div v-if="simulated.totalPayments !== 1" class="flex w-full items-center justify-between">
            <p class="text-sm text-grey-2/80">{{ $t('simulation.summary.last_payment') }}</p>
            <p class="text-sm font-bold text-grey-2/80 lg:font-semibold lg:text-grey-2">
              {{ $t('wording.after') }} {{ monthFormat(duration) }}
            </p>
          </div>
        </div>
        <!-- No fee -->
        <div class="mb-8 rounded-2xl bg-green-7 p-4 text-sm font-semibold text-grey-2/80 lg:mb-12">
          {{ $t('simulation.summary.no_fees') }}
        </div>
        <!-- Invest button -->
        <div
          v-if="!offer && (project.state === 'campaign' || project.state === 'reserved')"
          class="mx-auto mb-8 mt-12 lg:flex"
        >
          <div class="group relative">
            <GpButton
              :disabled="investDisabled"
              soft-disabled
              @click="invest"
            >
              {{ $t('wording.invest') }}
            </GpButton>
            <div v-if="investDisabled" class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-56 -translate-x-1/2 rounded-xl bg-grey-2 px-3 py-2 text-center text-xs text-white opacity-0 shadow-tooltip transition-opacity group-hover:opacity-100">
              {{ $t('simulation.invest_info') }}
              <span class="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-[#4F5963]"></span>
            </div>
          </div>
        </div>
        <router-link v-if="offer" :to="'/mkt-purchase/' + slug">
          <GpButton class="mx-auto mb-8" desktopWidth="w-1/2">
            {{ $t('wording.purchase') }}
          </GpButton>
        </router-link>
        <!-- Payment plan -->
        <p class="mb-2 text-left text-sm font-bold text-grey-2 lg:mb-4">
          {{ $t('simulation.payment_plan.title') }}
        </p>
        <p
          class="mb-6 text-left text-sm text-grey-2/80 lg:mb-8"
        >
          {{
            $t('simulation.payment_plan.' + paymentPlanVariant, {
              during_grace: dollarFormatting(simulated.payDuringGrace),
              after_grace: dollarFormatting(simulated.payAfterGrace),
              last_payment: dollarFormatting(simulated.lastPayment),
              periodicity: periodicityText,
              duration: monthFormat(duration),
            })
          }}
        </p>
        <!-- Timeline -->
        <PaymentPlanStep
          :description="$t('simulation.payment_plan.investment')"
          :date="'today'"
          :amount="`$${amount}`"
          class="mb-8 lg:mb-6"
        />
        <PaymentPlanStep
          v-if="simulated.gracePayments > 0"
          :description="$t('simulation.payment_plan.first_payment_interest')"
          :date="
            monthFormat(12 / project.funding_rate.periodicity) +
            ' ' +
            $t('simulation.payment_plan.after_funded')
          "
          :amount="`+ $${dollarFormatting(simulated.payDuringGrace)}`"
          isGreen
          class="mb-8 lg:mb-6"
        />
        <PaymentPlanStep
          v-if="simulated.totalPayments !== 1"
          :description="$t('simulation.payment_plan.first_payment_capital')"
          :date="
            monthFormat(project.funding_rate.grace_period_months) +
            ' ' +
            $t('simulation.payment_plan.after_funded')
          "
          :amount="`+ $${dollarFormatting(simulated.payAfterGrace)}`"
          isGreen
          class="mb-8 lg:mb-6"
        />
        <PaymentPlanStep
          :description="
            simulated.totalPayments !== 1
              ? $t('simulation.payment_plan.last_payment_capital')
              : $t('simulation.payment_plan.only_payment')
          "
          :date="monthFormat(duration) + ' ' + $t('simulation.payment_plan.after_funded')"
          :amount="`+ $${dollarFormatting(simulated.lastPayment ?? simulated.payAfterGrace)}`"
          isGreen
        />
        <!-- Download -->
        <div class="button-padding-bottom">
          <div class="mt-6 flex items-center justify-between rounded-2xl bg-white p-4 lg:mt-12">
            <p class="text-sm font-semibold text-grey-2/80">
              {{ $t('simulation.payment_plan.title') }}
            </p>
            <a class="orange-link flex" href="#" @click.prevent="downloadXls">
              <img src="@/assets/svg/arrows/down_orange_arrow.svg" alt="down" class="mr-2" />
              <p>{{ $t('wording.download') }}</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { computed, onMounted, ref, watch } from 'vue'
import axios from 'axios'
import Back from '@/components/general/Back.vue'
import GpButton from '@/components/general/GpButton.vue'
import { Form } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import GeneralInput from '@/components/general/Input.vue'
import PaymentPlanStep from '@/components/simulation/PaymentPlanStep.vue'
import useGlobalHelpers from '@/mixins/useGlobalHelpers.js'
export default {
  components: {
    Back,
    GpButton,
    GeneralInput,
    Form,
    PaymentPlanStep,
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const form = ref(null)
    const amount = ref(null)
    const simulated = ref(null)
    const isValid = ref(false)

    const i18n = useI18n({ useScope: 'global' })
    const {
      dollarFormatting,
      monthFormat,
      dollarToNumberFormatting,
      slugIt,
    } = useGlobalHelpers()

    onMounted(async () => {
      const routeProjectId = route.params.id.split('-').pop()
      if (!store.state.projects.project || String(store.state.projects.project.id) !== routeProjectId) {
        await store.dispatch('setRequestSettings', 'min_investment')
        await store.dispatch('getProject', routeProjectId)
      }

      if (route.query.offer) {
        if (!store.state.offers.offer) await store.dispatch('fetchOffer', route.query.offer)
        amount.value = dollarFormatting(parseInt(route.query.amount))
        simulate(true)
      }
    })

    watch(
      () => amount.value,
      (val) => {
        if (!dollarToNumberFormatting(val, true)) {
          simulated.value = null
          return
        }
        simulate()
      }
    )

    const project = computed(() => {
      return store.state.projects.project
    })

    const profile = computed(() => {
      return store.getters.getProfile()
    })

    const investDisabled = computed(() => {
      return profile.value.kyc_level !== 'regular' || !profile.value.dealer_approved || profile.value.expired_suitability
    })

    const remainingTime = computed(() => {
      if (store.state.offers.offer)
        return (
          store.state.offers.offer.remaining_payments_count *
          (12 / store.state.projects.project.funding_rate.periodicity)
        )
      else return null
    })

    const simulatedAmountRules = computed(() => {
      const projectMaxAmount = project.value.max_crowdlending_value
      const minInvestmentCrowdlending = settings.value?.min_investment ?? 1000
      return `required|dollar|minValue:${minInvestmentCrowdlending}|maxValue:${projectMaxAmount}`
    })

    const settings = computed(() => {
      return store.state.general.settings
    })

    const xlsParams = ref(null)

    const simulation = async (investmentValue) => {
      const investment = dollarToNumberFormatting(investmentValue)
      xlsParams.value = {
        project_id: project.value.id,
        amount: investment,
      }
      const response = await axios.get(
        process.env.VUE_APP_SERVER_BASE_URL + 'api/v1/investment_simulation',
        { params: xlsParams.value }
      )
      return response.data
    }

    const simulate = async (skipValidation) => {
      const validation = await form.value.validate()
      isValid.value = validation.valid || skipValidation

      if (!validation.valid && !skipValidation) {
        return
      }

      try {
        simulated.value = await simulation(amount.value)
      } catch {
        // Network/server error — leave simulated unchanged
      }
    }

    const downloadXls = async () => {
      const response = await axios.get(
        process.env.VUE_APP_SERVER_BASE_URL + 'api/v1/investment_simulation/xls',
        { params: xlsParams.value, responseType: 'blob' }
      )
      const url = URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = 'InvestmentSimulation.xlsx'
      link.click()
      URL.revokeObjectURL(url)
    }

    const periodicityText = computed(() => {
      return i18n.t('simulation.payment_plan.periodicity.' + project.value.funding_rate.periodicity)
    })

    const paymentPlanVariant = computed(() => {
      if (!simulated.value) return null
      if (simulated.value.totalPayments === 1) return 'text_single_pay'
      if (simulated.value.gracePayments > 0)
        return simulated.value.lastPayment ? 'text_w_grace_balloon' : 'text_w_grace'
      return simulated.value.lastPayment ? 'text_wo_grace_balloon' : 'text_wo_grace'
    })

    const invest = () => {
      router.push({
        name: 'invest-wallet',
        params: {
          project: project.value.id,
          simulationAmount: amount.value,
        },
      })
    }

    const slug = computed(() => {
      return project.value.name
        ? slugIt(project.value.name) + '-' + route.query.offer
        : route.query.offer
    })

    const duration = computed(() => {
      return remainingTime.value ?? project.value.funding_rate.duration_months
    })

    return {
      project,
      profile,
      investDisabled,
      form,
      simulate,
      amount,
      simulated,
      dollarFormatting,
      monthFormat,
      periodicityText,
      paymentPlanVariant,
      invest,
      offer: route.query.amount,
      slug,
      remainingTime,
      duration,
      simulatedAmountRules,
      isValid,
      downloadXls,
    }
  },
}
</script>
